import React, { ReactNode, useState } from 'react';
import { Button, Modal, Stack, Form, Row, Col } from 'react-bootstrap';
import {
	useBucketUrlMutation,
	useUpdateUserDetailsMutation,
	useUploadFileMutation,
} from 'services';
import { useFormik } from 'formik';
import { resumeInitialValues, resumeSchema } from 'constants/schemaValidations';
import { ResumeInterface } from 'constants/interfaces';
import toast from 'react-hot-toast';
import useTranslate from 'hooks/useTranslate';
import Loader from 'components/Common/Loader';
import { isValidFileSize, isValidFileType } from 'utils';
import useCommonStore from 'store/common';

const EditResume: React.FC<any> = () => {
	const { translate } = useTranslate();
	// state
	const [isUploading, setIsUploading] = useState(false);
	const [fileData, setFileData] = useState<File | null>(null);
	const [signurlData, setSignurlData] = useState<any>(null);
	const { modalConfig = {} }: any = useCommonStore((state) => state);

	// mutation
	const { mutateAsync: bucketUrlMutation } = useBucketUrlMutation();
	const { mutateAsync: uploadFileMutation } = useUploadFileMutation();
	const { mutateAsync: updateProfile } = useUpdateUserDetailsMutation();

	const formik = useFormik({
		initialValues: resumeInitialValues,
		enableReinitialize: true,
		validationSchema: resumeSchema(translate),
		onSubmit: async (values) => {
			setIsUploading(true);
			if (fileData && signurlData) {
				const requestOptions = {
					method: 'PUT',
					headers: {
						'Content-Type': fileData?.type,
					},
					body: fileData,
					redirect: 'follow',
				};

				await uploadFileMutation({
					url: signurlData?.url,
					requestOptions,
				});

				updateProfile({ resume: values, saveType: 'resume' })
					.then((res: any) => {
						if (res?.success) {
							toast.success(res?.message);
						}
					})
					.finally(() => {
						setIsUploading(false);
						modalConfig?.onClose();
					});
			}
		},
	});

	const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		setSignurlData(null);
		setFileData(null);
		setIsUploading(true);
		const file: any = event.target.files?.[0];
		if (file) {
			if (!isValidFileType(file) || !isValidFileSize(file)) {
				setIsUploading(false);
				return;
			}

			const response: any = await bucketUrlMutation({
				extension: [file.name.split('.').pop()],
				type: 'RESUME',
				originalFileName: [file.name],
				contentType: [file.type],
			});
			setFileData(file);
			if (response?.data?.length > 0) {
				setSignurlData(response?.data[0]);
				formik.setFieldValue('path', response?.data?.[0]?.location || '');
				formik.setFieldValue('title', response?.data?.[0]?.fileName || '');
			}
		}
		setIsUploading(false);
	};

	const renderError = <T extends keyof ResumeInterface>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	return (
		<Modal.Body>
			<Button
				className="close-btn rounded-circle border-0 fw-bold position-absolute"
				onClick={() => modalConfig?.onClose()}
			>
				<i className="ri-close-line"></i>
			</Button>
			<Form className="edit-form" onSubmit={formik.handleSubmit}>
				<Stack className="gap-4">
					<h4 className="fw-bold mb-0">{translate('Common.uploadResume')}</h4>
					<Row>
						<Col md="12">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Profile.uploadResume')}</Form.Label>
								<Form.Control
									type="file"
									placeholder="Upload Resume"
									className="rounded-pill"
									name="path"
									accept=".pdf, .doc, .docx"
									onChange={handleFileChange}
									onClick={(e: any) => {
										e.target.value = null;
									}}
									onBlur={formik.handleBlur}
								/>
								{formik.submitCount > 0 && !formik.values.path && renderError('path')}
							</Form.Group>
						</Col>
					</Row>
					<div className="edit-modal-actions d-flex gap-4">
						{isUploading ? (
							<Loader />
						) : (
							<Button className="filled-btn rounded-pill" type="submit">
								{translate('Common.save')}
							</Button>
						)}
					</div>
				</Stack>
			</Form>
		</Modal.Body>
	);
};

export default EditResume;
